import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { ClipboardModule, Clipboard } from '@angular/cdk/clipboard';
import { MatButtonModule } from '@angular/material/button';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { OKTA_AUTH } from '@okta/okta-angular';
import { OktaAuth } from '@okta/okta-auth-js';

import * as fromRoot from '../reducers';
import { UserLocation } from '../models/user/location.model';
import {
  LOCATION_SCENARIO_LABELS,
  LocationScenario,
  LocationScenarioService,
} from '../core/location-scenario';

@Component({
  selector: 'spot-admin-debug-panel',
  standalone: true,
  imports: [CommonModule, MatButtonModule, MatSnackBarModule, ClipboardModule],
  templateUrl: './admin-debug-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./admin-debug-panel.component.scss'],
})
export class AdminDebugPanelComponent {
  public expanded = false;

  public readonly vm$: Observable<{
    isAdmin: boolean;
    adminFull: boolean;
    adminView: boolean;
    userName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    guid?: string;
    location: UserLocation | null;
  }>;

  constructor(
    private readonly store: Store<fromRoot.State>,
    @Inject(OKTA_AUTH) private readonly oktaAuth: OktaAuth,
    private readonly clipboard: Clipboard,
    private readonly snackBar: MatSnackBar,
    private readonly locationScenario: LocationScenarioService,
  ) {
    this.vm$ = combineLatest([
      this.store.select(fromRoot.isAdminUser),
      this.store.select(fromRoot.getAdminFull),
      this.store.select(fromRoot.getAdminView),
      this.store.select(fromRoot.getUserState),
      this.store.select(fromRoot.getSelectedLocation),
    ]).pipe(
      map(([isAdmin, adminFull, adminView, user, location]) => ({
        isAdmin,
        adminFull,
        adminView,
        userName: user.userName,
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        guid: user.guid,
        location,
      }))
    );
  }

  public readonly scenarios: { id: LocationScenario; label: string }[] = [
    { id: 'favorite-unavailable', label: LOCATION_SCENARIO_LABELS['favorite-unavailable'] },
    { id: 'all-unavailable', label: LOCATION_SCENARIO_LABELS['all-unavailable'] },
  ];

  public canSimulate(isAdmin: boolean): boolean {
    return this.locationScenario.canSimulate(isAdmin);
  }

  /** Label of the scenario applied to this page load, for the "Simulating" note. */
  public get simulatingLabel(): string | null {
    const active = this.locationScenario.active;
    return active ? LOCATION_SCENARIO_LABELS[active] : null;
  }

  public simulate(scenario: LocationScenario): void {
    this.locationScenario.setScenario(scenario);
    this.reload();
  }

  public turnOffSimulation(): void {
    this.locationScenario.clearScenario();
    // Leave the error page: a plain reload would land on the same unavailable location.
    this.goHome();
  }

  // A full reload so guards and user setup run exactly as they do in production.
  protected reload(): void {
    globalThis.location.reload();
  }

  // Full page load of the homepage so user setup and guards re-resolve a default location.
  protected goHome(): void {
    globalThis.location.assign('/');
  }

  public toggle(): void {
    this.expanded = !this.expanded;
  }

  public copyAccessToken(): void {
    const token = this.oktaAuth.getAccessToken();
    if (!token) {
      this.snackBar.open('No access token available.', 'Dismiss', { duration: 4000 });
      return;
    }
    const copied = this.clipboard.copy(token);
    this.snackBar.open(
      copied ? 'Access token copied to clipboard.' : 'Could not copy to clipboard.',
      'Dismiss',
      { duration: 4000 }
    );
  }
}
